import { Stack, Typography } from '@mui/material'
import {
    DataGridPro,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridRowSelectionModel,
    GridSortModel,
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import { useCallback, useMemo, useState } from 'react'
import { useBulkActionContext } from 'client/candidate-table-temp/hooks/useBulkActionContext'
import { useCandidateSqlSearch } from 'client/candidate-table-temp/hooks/useCandidateSqlSearch'
import { useRosterCandidatesColumns } from 'client/candidate-table-temp/hooks/useRosterCandidatesColumns'
import { type RosterCandidateTableRowModel } from 'client/candidate-table-temp/hooks/useRosterSearchWithFilters'
import { getPxAsRem } from 'shared/utils'
import { generateRowId } from './utils'

interface CandidatesTablePaginatedProps {
    data: RosterCandidateTableRowModel[]
    loading: boolean
    totalItems?: number
    page: number
    limit: number
    onPaginationChange: (model: { page: number; pageSize: number }) => void
}

// Splits selected row IDs into [candidateIds, rosterCandidateIds]
function parseSelectedRowIds(rowIds: string[]): [string[], string[]] {
    return rowIds.reduce<[string[], string[]]>(
        (acc, rowId) => {
            const [id, rosterCandidateId] = rowId.split('/')
            acc[0].push(id)
            if (rosterCandidateId) {
                acc[1].push(rosterCandidateId)
            }
            return acc
        },
        [[], []]
    )
}

interface TableToolbarProps {
    loading: boolean
    selectionCount: number
    totalCount: number
}

function TableToolbar({
    loading,
    selectionCount,
    totalCount,
}: TableToolbarProps) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={getPxAsRem(24)}
            padding={getPxAsRem(16)}
        >
            <Typography noWrap>
                {loading
                    ? 'Fetching results...'
                    : `${selectionCount} selected of ${totalCount} results`}
            </Typography>
        </Stack>
    )
}

const pinnedColumnsList = [
    'rowNumber',
    'name',
    'firstName',
    'lastName',
    'colors',
    'rosterCandidateFavoriteCount',
    'processStatus',
]

export default function RosterCandidatesTablePaginated({
    data,
    loading,
    page,
    totalItems,
    limit,
    onPaginationChange,
}: CandidatesTablePaginatedProps) {
    const disableSelection = true
    const hideToolbar = true

    const apiRef = useGridApiRef()
    const [selectedRowIds, setSelectedRowIds] = useState<string[]>([])

    const { isLoading: bulkActionLoading } = useBulkActionContext()
    const { setActiveSortFields } = useCandidateSqlSearch()
    const { columnVisibilityModel, columnDef } = useRosterCandidatesColumns()

    const [sortModel, setSortModel] = useState<GridSortModel>([])

    const handleSortModelChange = useCallback(
        (newSortModel: GridSortModel) => {
            setSortModel(newSortModel)

            if (newSortModel.length === 0) {
                setActiveSortFields([])
                return
            }

            const sortItem = newSortModel[0]
            const column = columnDef.find((col) => col.field === sortItem.field)

            if (!column?.sortIndexField) {
                return
            }

            setActiveSortFields([
                {
                    field: column.sortIndexField,
                    order: sortItem.sort as 'asc' | 'desc',
                },
            ])
        },
        [columnDef, setActiveSortFields]
    )

    const [selectedCandidateIds] = useMemo(
        () => parseSelectedRowIds(selectedRowIds),
        [selectedRowIds]
    )

    const handleRowSelectionChange = useCallback(
        (model: GridRowSelectionModel) => {
            const rowIds = Array.from(model).map((id) => id.toString())
            setSelectedRowIds(rowIds)
        },
        []
    )

    const renderToolbar = useCallback(
        () => (
            <TableToolbar
                loading={loading}
                selectionCount={selectedCandidateIds.length}
                totalCount={totalItems ?? 0}
            />
        ),
        [loading, selectedCandidateIds, totalItems]
    )

    const pinnedColumns = useMemo(() => {
        const cols = pinnedColumnsList || [
            'rowNumber',
            'name',
            'firstName',
            'lastName',
            'colors',
            'rosterCandidateFavoriteCount',
            'actions',
            'processStatus',
        ]
        if (!disableSelection) {
            cols.unshift(GRID_CHECKBOX_SELECTION_COL_DEF.field)
        }
        return { left: cols }
    }, [disableSelection])

    return (
        <DataGridPro
            apiRef={apiRef}
            rows={data}
            columns={columnDef}
            getRowId={generateRowId}
            columnVisibilityModel={columnVisibilityModel}
            checkboxSelection={!disableSelection}
            keepNonExistentRowsSelected
            rowSelectionModel={selectedRowIds}
            onRowSelectionModelChange={handleRowSelectionChange}
            pagination
            paginationMode="server"
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            rowCount={totalItems ?? 0}
            paginationModel={{
                page: page - 1,
                pageSize: limit,
            }}
            onPaginationModelChange={(model) => {
                if (model.page === 0 && (totalItems ?? 0) === 0) {
                    return
                }
                onPaginationChange(model)
            }}
            pageSizeOptions={[25, 50, 100]}
            loading={loading || bulkActionLoading}
            getRowHeight={() => 65}
            disableColumnMenu
            disableRowSelectionOnClick
            disableColumnReorder
            sx={{
                height: '100%',
                overflow: 'auto',
                minHeight: data && data.length > 0 ? undefined : '40vh',
                boxSizing: 'content-box',
            }}
            pinnedColumns={pinnedColumns}
            slots={
                hideToolbar
                    ? undefined
                    : {
                          toolbar: renderToolbar,
                      }
            }
        />
    )
}
